import { Tooltip, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import BigNumber from 'bignumber.js';
import React from 'react';

import { ReactComponent as BscIcon } from '../../../common/svg/button-icons/bsc-circle.svg';
import { ReactComponent as EthIcon } from '../../../common/svg/button-icons/eth-circle-icon.svg';
import styles from './OrderNotificationsModal.module.css';

export interface OrderNotificationsModalProps {
  orders: any[];
}

export interface OrderNotificationsProps extends OrderNotificationsModalProps {
  hideModal: () => void;
  orders: any[];
}

interface OrderNotificationsHeaderProps {
  hideModal: () => void;
}

const OrderNotificationsHeader = ({
  hideModal,
}: OrderNotificationsHeaderProps) => (
  <div className={`flex justify-between items-center gap-6`}>
    <h2 className={`fontVanguard font-bold text-4xl m-0 mt-2`}>
      Your Items’ orders
    </h2>
    <div
      role="button"
      tabIndex={0}
      onClick={hideModal}
      onKeyDown={hideModal}
      className={`cursor-pointer`}
    >
      <CloseIcon />
    </div>
  </div>
);

interface OrderProps {
  pictureUrl: string;
  title: string;
  contractAddress: string;
  nftId: number;
  amount: number;
  price: BigNumber;
  network: string;
}

const Order = ({
  pictureUrl,
  title,
  contractAddress,
  nftId,
  amount,
  price,
  network,
}: OrderProps) => {
  const onOrderClick = () => {
    window.open(`/platform/nftDetails/${contractAddress}/${nftId}`, '_blank');
  };

  return (
    <div
      tabIndex={0}
      role="button"
      onClick={onOrderClick}
      onKeyPress={onOrderClick}
      className={`flex justify-between gap-6 ${styles.order}`}
    >
      <div className={`flex gap-6 w-full`}>
        <img src={pictureUrl} alt={title} className={styles.orderImage} />
        <div className={`flex flex-col gap-1 w-full`}>
          <div
            className={`flex justify-between flex-col gap-0 md:gap-12 md:flex-row `}
          >
            <Typography variant="h6">
              {title} × {amount}
            </Typography>
            <Tooltip
              title={<p style={{ fontSize: 16 }}>{price.toString(10)} MOGUL</p>}
              key="price"
            >
              <Typography variant="h6" className={styles.price}>
                {price.toFixed(2, 1)}
                {price.isLessThan('0.01') ? '…' : ''} MOGUL
              </Typography>
            </Tooltip>
          </div>
          <div className={`flex gap-2 items-center`}>
            {['MAINNET'].includes(network) ? (
              <EthIcon height="20" width="20" className="b-0 r-0" />
            ) : (
              <BscIcon height="20" width="20" className="b-0 r-0" />
            )}
            <Typography variant="body1">
              {`${contractAddress.slice(0, 4)}...${contractAddress.slice(-4)}`}
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};

const MAX_TITLE_CHARS = 12;

const Orders = ({ orders }: { orders: any[] }) => {
  return (
    <div>
      {orders.map(order => {
        const title = order.nft?.title;

        return (
          <Order
            key={order.id}
            pictureUrl={order.nft?.image?.publicUrl}
            title={`${title.slice(0, MAX_TITLE_CHARS)}${
              title.length > MAX_TITLE_CHARS ? '...' : ''
            }`}
            contractAddress={order.nft?.contractAddress}
            nftId={order.nft?.id}
            amount={order.parameters?.consideration?.[0]?.startAmount}
            price={new BigNumber(order.parameters?.offer?.[0]?.startAmount).div(
              new BigNumber(10).pow(18),
            )}
            network={order.nft.chainId}
          />
        );
      })}
    </div>
  );
};

const OrderNotifications = ({ hideModal, orders }: OrderNotificationsProps) => (
  <div className={`${styles.orderNotificationsModal}`}>
    <OrderNotificationsHeader hideModal={hideModal} />
    <Orders orders={orders} />
  </div>
);

export const OrderNotificationsModal =
  (props: OrderNotificationsModalProps) =>
  ({ hideModal }: OrderNotificationsProps) => {
    return <OrderNotifications hideModal={hideModal} {...props} />;
  };
